import React from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { TimerMode } from './types'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { TimerCurrentStatusSection } from './TimerCurrentStatusSection'
import { Pause, Play } from 'lucide-react'

interface TimerControlsProps {
  status: {
    mode: string
    currentRound: number
    totalRounds: number
    currentPhase: string
    timeRemaining: number
  } | null
  endTime: string | null
  phaseDuration: number | null
  phaseStatus: ActivityState | null
  roundStatus: ActivityState | null
  mode: TimerMode
  loading: boolean
  controlsLoading: {
    startNextRound: boolean
    endRound: boolean
    endPhase: boolean
    previousPhase: boolean
    pauseGame: boolean
  }
  timeControlsEnabled: boolean
  onTimeControlsToggle: (enabled: boolean) => void
  onStartNextRound: () => void
  onEndRound: () => void
  onPause: () => void
  onEndPhase: () => void
  onPreviousPhase: () => void
  canGoPreviousPhase: boolean
  isGameCompleted: boolean
  isGameInProgress: boolean
  isInfinitePhase: boolean
  hideStatusSection?: boolean
}

export const TimerControls: React.FC<TimerControlsProps> = ({
  status,
  endTime,
  phaseStatus,
  roundStatus,
  mode,
  loading,
  controlsLoading,
  timeControlsEnabled,
  onTimeControlsToggle,
  onStartNextRound,
  onEndRound,
  onPause,
  onEndPhase,
  onPreviousPhase,
  canGoPreviousPhase,
  isGameCompleted,
  isGameInProgress,
  isInfinitePhase,
  hideStatusSection = false,
}) => {
  if (!status) {
    return (
      <div className='p-6 text-center'>
        <p>Loading timer status...</p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {!hideStatusSection && (
        <TimerCurrentStatusSection
          status={status}
          mode={mode}
          endTime={endTime}
          phaseStatus={phaseStatus}
          roundStatus={roundStatus}
          isInfinitePhase={isInfinitePhase}
        />
      )}

      {!isGameCompleted && isGameInProgress && (
        <div>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-medium text-dark-blue'>
              Timer Controls
            </h3>
            <div className='flex items-center space-x-2'>
              <Label
                htmlFor='time-controls-toggle'
                className='font-medium text-dark-blue'
              >
                Enable Time Controls
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Switch
                      id='time-controls-toggle'
                      checked={timeControlsEnabled}
                      onCheckedChange={onTimeControlsToggle}
                      disabled={mode === TimerMode.MANUAL_MODE}
                      className='bg-gray-400'
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {mode === TimerMode.MANUAL_MODE
                        ? 'Time controls are not available in manual mode'
                        : 'Enable or disable timer controls for managing game phases'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          {timeControlsEnabled && (
            <TooltipProvider>
              <div className='flex gap-4'>
                {mode !== TimerMode.SINGLE_ROUND &&
                  mode !== TimerMode.SINGLE_ROUND_ADVISOR_ONLY && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                          onClick={onStartNextRound}
                          disabled={
                            loading ||
                            controlsLoading.startNextRound ||
                            status.totalRounds === status.currentRound
                          }
                        >
                          {controlsLoading.startNextRound
                            ? 'Starting...'
                            : 'Start Next Round'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Start the next round of the game</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                      onClick={onEndRound}
                      disabled={
                        loading ||
                        controlsLoading.endRound ||
                        roundStatus === ActivityState.Completed
                      }
                    >
                      {controlsLoading.endRound ? 'Ending...' : 'End Round'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>End the current round</p>
                  </TooltipContent>
                </Tooltip>
                {mode !== TimerMode.MANUAL_MODE && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                        onClick={onPreviousPhase}
                        disabled={
                          loading ||
                          controlsLoading.previousPhase ||
                          !canGoPreviousPhase ||
                          roundStatus === ActivityState.Completed
                        }
                      >
                        {controlsLoading.previousPhase
                          ? 'Rewinding...'
                          : 'Previous Phase'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Return to the previous phase and restart its timer</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {mode !== TimerMode.MANUAL_MODE && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                        onClick={onEndPhase}
                        disabled={
                          loading ||
                          controlsLoading.endPhase ||
                          roundStatus === ActivityState.Completed
                        }
                      >
                        {controlsLoading.endPhase ? 'Ending...' : 'End Phase'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>End the current phase and move to the next phase</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className={`${
                        roundStatus === ActivityState.Paused
                          ? 'text-white bg-green-600 hover:bg-green-700'
                          : 'text-white bg-orange-600 hover:bg-orange-700'
                      } transition-colors duration-200`}
                      onClick={onPause}
                      disabled={loading || controlsLoading.pauseGame}
                    >
                      {controlsLoading.pauseGame ? (
                        <div className='w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                      ) : roundStatus === ActivityState.Paused ? (
                        <Play className='w-4 h-4 fill-white' />
                      ) : (
                        <Pause className='w-4 h-4 fill-white' />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {controlsLoading.pauseGame
                        ? roundStatus === ActivityState.Paused
                          ? 'Resuming...'
                          : 'Pausing...'
                        : roundStatus === ActivityState.Paused
                          ? 'Resume the game'
                          : 'Pause the game'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
          {!timeControlsEnabled && (
            <p className='italic text-gray-500'>
              Time controls are currently disabled. Enable them to control the
              timer.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
